import {
  type AgChartTheme,
  type AgChartThemeOverrides,
} from 'ag-charts-community/typings/main'
import { themeV3 } from '../../../theme'
import { tokens } from '../../../tokens'
import { paletteV3 } from '../../../tokens/colors.v3'
import { getColors } from '../../Chart/colors'

export type CustomChartThemeId = 'tokenterminalLight' | 'tokenterminal'

// "undetermined" is a loader theme. Sort of "none" theme
export const chartThemes: (CustomChartThemeId | 'undetermined')[] = [
  'undetermined',
  'tokenterminal',
  'tokenterminalLight',
]

const fontFamily = themeV3.fonts.primary

/*
 * Highlight styles
 */

const higlightStyleDark = {
  item: {
    fillOpacity: 0.1,
    fill: 'white',
  },
  series: {
    dimOpacity: 0.5,
  },
}

const highlightStyleLight = {
  item: {
    fillOpacity: 0.1,
    fill: 'black',
  },
  series: {
    dimOpacity: 0.5,
  },
}

/*
 * Category axes
 */
const commonAxesCategoryDark = {
  label: {
    color: tokens.onSecondary.dark,
  },
  gridStyle: [
    {
      stroke: tokens.secondaryBorder.dark,
      lineDash: 0,
    },
    {
      stroke: tokens.secondaryBorder.dark,
      lineDash: 0,
    },
  ],
  line: {
    color: tokens.secondaryBorder.dark,
  },
  tick: {
    color: tokens.secondaryBorder.dark,
  },
}

const commonAxesCategoryLight = {
  label: {
    color: tokens.onSecondary.light,
  },
  gridStyle: [
    {
      stroke: tokens.secondaryBorder.light,
      lineDash: 0,
    },
    {
      stroke: tokens.secondaryBorder.light,
      lineDash: 0,
    },
  ],
  line: {
    color: tokens.secondaryBorder.light,
  },
  tick: {
    color: tokens.secondaryBorder.light,
  },
}

/*
 * Value axes
 */
const commonAxesValueDark = {
  label: {
    color: tokens.onSecondary.dark,
  },
  gridStyle: [
    {
      stroke: tokens.secondaryBorder.dark,
      lineDash: 0,
    },
    {
      stroke: tokens.secondaryBorder.dark,
      lineDash: 0,
    },
  ],
  line: {
    color: tokens.secondaryBorder.dark,
  },
  tick: {
    color: tokens.secondaryBorder.dark,
  },
}

const commonAxesValueLight = {
  label: {
    color: tokens.onSecondary.light,
  },
  gridStyle: [
    {
      stroke: tokens.secondaryBorder.light,
      lineDash: 0,
    },
    {
      stroke: tokens.secondaryBorder.light,
      lineDash: 0,
    },
  ],
  line: {
    color: tokens.secondaryBorder.light,
  },
  tick: {
    color: tokens.secondaryBorder.light,
  },
}

export const customChartThemes: {
  [key in CustomChartThemeId | 'undetermined']: Exclude<AgChartTheme, string>
} = {
  undetermined: {
    // Loader theme to reduce flashing on load
    palette: {
      fills: ['transparent'],
      strokes: ['transparent'],
    },
    overrides: {
      common: {
        background: {
          fill: 'transparent',
        },
        title: {
          color: 'transparent',
        },
        legend: {
          item: {
            label: {
              color: 'transparent',
            },
          },
        },
      },
    },
  },
  // Dark default theme
  tokenterminal: {
    palette: {
      fills: getColors(10),
      strokes: ['transparent'],
    },
    overrides: {
      common: {
        background: {
          // Table header color. Light theme different on purpose
          fill: paletteV3.grey5,
        },
        title: {
          color: tokens.onPrimary.dark,
        },
        legend: {
          item: {
            label: {
              color: tokens.onSecondary.dark,
            },
          },
        },
        axes: {
          category: commonAxesCategoryDark,
          groupedCategory: commonAxesCategoryDark,
          number: commonAxesValueDark,
          time: commonAxesValueDark,
        },
      },
      column: {
        series: {
          highlightStyle: higlightStyleDark,
        },
      },
      bar: {
        series: {
          highlightStyle: higlightStyleDark,
        },
      },
      area: {
        series: {
          highlightStyle: higlightStyleDark,
        },
      },
      pie: {
        series: {
          highlightStyle: higlightStyleDark,
          sectorLabel: {
            color: tokens.primary.dark,
          },
          calloutLabel: {
            color: tokens.onSecondary.dark,
          },
          title: {
            color: tokens.onSecondary.dark,
          },
        },
      },
      histogram: {
        series: {
          highlightStyle: higlightStyleDark,
        },
      },
    },
  },
  tokenterminalLight: {
    palette: {
      fills: getColors(10),
      strokes: ['transparent'],
    },
    overrides: {
      common: {
        background: {
          fill: tokens.primary.light,
        },
        title: {
          color: tokens.onPrimary.light,
        },
        legend: {
          item: {
            label: {
              color: tokens.onPrimary.light,
            },
          },
        },
        axes: {
          category: commonAxesCategoryLight,
          groupedCategory: commonAxesCategoryLight,
          number: commonAxesValueLight,
          time: commonAxesValueLight,
        },
      },
      column: {
        series: {
          highlightStyle: highlightStyleLight,
        },
      },
      bar: {
        series: {
          highlightStyle: highlightStyleLight,
        },
      },
      area: {
        series: {
          highlightStyle: highlightStyleLight,
        },
      },
      pie: {
        series: {
          highlightStyle: highlightStyleLight,
          sectorLabel: {
            color: tokens.primary.light,
          },
          calloutLabel: {
            color: tokens.onSecondary.light,
          },
          title: {
            color: tokens.onSecondary.light,
          },
        },
      },
      histogram: {
        series: {
          highlightStyle: highlightStyleLight,
        },
      },
    },
  },
}

export const chartThemeOverrides: AgChartThemeOverrides = {
  common: {
    title: {
      enabled: false, // Limited styling. Title can't have paddingTop set here. If enabled by default, then it's correct, but that would mean enabling title always for all charts
      fontWeight: 600,
      fontSize: 18,
      fontFamily,
      spacing: 10,
    },
    legend: {
      position: 'bottom',
      spacing: 16,
      item: {
        label: {
          fontWeight: 400,
          fontSize: 12,
          fontFamily,
        },
        marker: {
          shape: 'square',
          size: 12,
          padding: 4,
          strokeWidth: 0,
        },
        paddingX: 16,
        paddingY: 8,
      },
    },
    axes: {
      category: {
        label: {
          fontFamily,
          formatter: ({ value }: { value: any }) =>
            typeof value === 'number' ? value : value || 'Unknown',
        },
      },
      number: {
        label: {
          fontFamily,
        },
      },
    },
  },
  // Most of below this dont work in common, or they dont have enough specificity to override default theme perhaps.
  column: {
    series: {
      strokeWidth: 0,
      fillOpacity: 0.9,
      label: {
        enabled: false,
        fontFamily,
        fontSize: 12,
      },
    },
  },
  bar: {
    series: {
      strokeWidth: 0,
      fillOpacity: 0.9,
      label: {
        enabled: false,
        fontFamily,
        fontSize: 12,
      },
    },
  },
  area: {
    series: {
      strokeWidth: 1,
      fillOpacity: 0.9,
      label: {
        enabled: false,
        fontFamily,
        fontSize: 12,
      },
    },
  },
  line: {
    series: {
      strokeWidth: 1.5,
      label: {
        enabled: false,
        fontFamily,
        fontSize: 12,
      },
    },
  },
  histogram: {
    series: {
      strokeWidth: 1.5,
      fillOpacity: 0.9,
      fill: '#C05F68',
      label: {
        fontFamily,
        fontSize: 12,
      },
    },
  },
  pie: {
    series: {
      sectorLabel: {
        fontFamily,
        fontWeight: 'bold',
      },
      calloutLabel: {
        fontFamily,
        formatter: ({ calloutLabelValue }: { calloutLabelValue?: string }) =>
          calloutLabelValue ?? 'Unknown',
      },
      title: {
        fontWeight: 'normal',
        fontSize: 14,
        fontFamily,
        spacing: 18,
      },
    },
  },
}
